import { ArticleReview } from "#/services/articleReviews";
import { formatDate } from "#/components/utls/time";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";

const fallbackValue = "—";

function resolveString(value: unknown) {
  if (typeof value === "string" && value.trim().length > 0) return value;
  return undefined;
}

function resolveDate(value: unknown) {
  if (typeof value === "string" && value.trim().length > 0) return value;
  return undefined;
}

export type ArticleReviewDisplay = {
  id: string;
  articleId?: string;
  revisionId?: string;
  status: string;
  createdAt?: string;
};

export function resolveArticleReviewDisplay(
  item: ArticleReview
): ArticleReviewDisplay {
  const id =
    resolveString(item.id) ||
    resolveString((item as { reviewId?: string }).reviewId) ||
    "";
  const articleId =
    resolveString(item.article_id) ||
    resolveString((item as { articleId?: string }).articleId) ||
    resolveString((item as { article_id?: string }).article_id);
  const revisionId =
    resolveString(item.revision_id) ||
    resolveString((item as { revisionId?: string }).revisionId) ||
    resolveString((item as { revision_id?: string }).revision_id);
  const status =
    resolveString(item.status) ||
    resolveString((item as { state?: string }).state) ||
    resolveString((item as { result?: string }).result) ||
    "Unknown";
  const createdAt =
    resolveDate(item.created_at) ||
    resolveDate((item as { createdAt?: string }).createdAt) ||
    resolveDate((item as { created_at?: string }).created_at) ||
    resolveDate((item as { updatedAt?: string }).updatedAt) ||
    resolveDate((item as { updated_at?: string }).updated_at);

  return { id, articleId, revisionId, status, createdAt };
}

type ArticleReviewListItemProps = {
  item: ArticleReview;
  showArticleId?: boolean;
  showRevisionId?: boolean;
  showActions?: boolean;
  onViewReview?: (reviewId: string) => void;
  expanded?: boolean;
  onToggleExpanded?: () => void;
};

export function ArticleReviewListItem({
  item,
  showArticleId = true,
  showRevisionId = true,
  showActions = false,
  onViewReview,
  expanded = false,
  onToggleExpanded,
}: ArticleReviewListItemProps) {
  const display = resolveArticleReviewDisplay(item);

  return (
    <tr className="border-t border-beige-100">
      <td className="px-4 py-3 text-brown-900">
        {display.id || fallbackValue}
      </td>
      {showArticleId ? (
        <td className="px-4 py-3 text-brown-700">
          <div className="max-w-[220px] truncate">
            {display.articleId || fallbackValue}
          </div>
        </td>
      ) : null}
      {showRevisionId ? (
        <td className="px-4 py-3 text-brown-700">
          <div className="max-w-[220px] truncate">
            {display.revisionId || fallbackValue}
          </div>
        </td>
      ) : null}
      <td className="px-4 py-3">
        <span className="inline-flex items-center rounded-full bg-beige-100 px-2 py-1 text-xs font-semibold text-brown-700">
          {display.status}
        </span>
      </td>
      <td className="px-4 py-3 text-brown-700">
        {display.createdAt ? formatDate(display.createdAt) : fallbackValue}
      </td>
      <td className="px-4 py-3 text-brown-700 align-top">
        {typeof item.notes === "string" && item.notes.trim().length > 0 ? (
          <div className="max-w-[520px] space-y-2">
            <p className="whitespace-pre-wrap text-sm text-brown-800">
              {expanded ? item.notes : `${item.notes.slice(0, 180)}${item.notes.length > 180 ? "…" : ""}`}
            </p>
            <ActionButton size="sm" tone="secondary" onClick={onToggleExpanded}>
              {expanded ? "Hide" : "Show full note"}
            </ActionButton>
          </div>
        ) : (
          <span>{fallbackValue}</span>
        )}
      </td>
      {showActions ? (
        <td className="px-4 py-3">
          <ActionButton
            size="sm"
            tone="secondary"
            onClick={() => onViewReview?.(display.id)}
            disabled={!display.id}
          >
            View details
          </ActionButton>
        </td>
      ) : null}
    </tr>
  );
}

export default ArticleReviewListItem;
